/* Divotory motion layer ("Tracer"). GSAP 3.15 + plugins and Lenis.
   Progressive enhancement: with reduced motion (OS or Settings), or if GSAP fails to load,
   nothing moves and everything is visible. Optional plugins load only where a page uses them. */
(() => {
  const root = document.documentElement;
  const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches || root.dataset.divotoryMotion === 'reduce';
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());
  const fine = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  let assets = {};
  try { assets = JSON.parse(document.getElementById('dx-assets')?.textContent || '{}'); } catch (e) { /* keep empty */ }
  const loading = {};
  const load = (...keys) => Promise.all(keys.map((k) => {
    if (loading[k]) return loading[k];
    loading[k] = new Promise((resolve) => {
      if (!assets[k]) return resolve(false);
      const s = document.createElement('script');
      s.src = assets[k]; s.async = true;
      s.onload = () => resolve(true); s.onerror = () => resolve(false);
      document.head.append(s);
    });
    return loading[k];
  }));

  ready(() => {
    const gsap = window.gsap;
    if (still() || !gsap || !window.ScrollTrigger) { root.classList.add('dx-go'); return; }
    const plugins = ['ScrollTrigger', 'SplitText', 'CustomEase', 'DrawSVGPlugin', 'MotionPathPlugin', 'ScrambleTextPlugin', 'Flip', 'Observer', 'ScrollToPlugin']
      .map((n) => window[n]).filter(Boolean);
    gsap.registerPlugin(...plugins);
    root.classList.add('dx-gsap');
    const { ScrollTrigger, SplitText, CustomEase, Flip, Observer } = window;
    const has = (n) => !!window[n];

    if (CustomEase) {
      CustomEase.create('dx-out', '0.16, 1, 0.3, 1');
      CustomEase.create('dx-in-out', '0.65, 0, 0.35, 1');
      CustomEase.create('dx-flight', 'M0,0 C0.12,0.36 0.3,0.62 0.5,0.72 0.68,0.8 0.82,0.9 1,1');
    }
    const ease = (n, fallback) => (CustomEase && CustomEase.get(n)) ? n : fallback;
    const OUT = ease('dx-out', 'expo.out');
    const FLIGHT = ease('dx-flight', 'power1.inOut');
    gsap.defaults({ ease: OUT, duration: 0.9 });

    /* store pages (product, cart, collection) scroll inside Horizon's .page-wrapper on wide screens */
    const wrap = (() => { const w = document.querySelector('.page-wrapper'); return w && getComputedStyle(root).overflowY === 'hidden' ? w : null; })();
    if (wrap) ScrollTrigger.defaults({ scroller: wrap });

    /* ---------- smooth scrolling (Lenis) on mouse and trackpad; touch keeps native momentum ---------- */
    let lenis = null;
    if (window.Lenis && fine() && !wrap) {
      lenis = new window.Lenis({ lerp: 0.12, smoothWheel: true, wheelMultiplier: 1, anchors: false, prevent: (node) => !!node.closest('dialog, .dx-menu-panel, [data-lenis-prevent]') });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
    }
    const scrollTo = (target, offset = 0) => {
      const head = parseFloat(getComputedStyle(root).getPropertyValue('--dx-head-h')) || 64;
      const box = wrap || window;
      if (lenis) lenis.scrollTo(target, { offset: -(head + 16) + offset, duration: 1.2, easing: (t) => 1 - Math.pow(1 - t, 4) });
      else if (has('ScrollToPlugin')) gsap.to(box, { duration: 1, ease: 'power3.inOut', scrollTo: { y: target, offsetY: head + 16, autoKill: true } });
      else if (typeof target === 'number') box.scrollTo({ top: target, behavior: 'smooth' });
      else target.scrollIntoView({ behavior: 'smooth' });
    };
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]:not([href="#"]):not([data-dx-totop])');
      if (!a || e.defaultPrevented) return;
      const t = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
      if (!t || a.closest('.dx-menu')) return;
      e.preventDefault();
      scrollTo(t);
      history.pushState(null, '', a.getAttribute('href'));
    });

    /* ---------- tracer art: draw the line, fly the ball, splash on landing ---------- */
    const flagWiggle = (svg) => {
      const flag = svg.querySelector('[data-dx-flag]');
      if (!flag) return;
      load('customwiggle').then(() => {
        if (!window.CustomWiggle) return;
        gsap.registerPlugin(window.CustomWiggle);
        if (!gsap.parseEase('dx-wave')) window.CustomWiggle.create('dx-wave', { wiggles: 6, type: 'uniform' });
        const pin = flag.getAttribute('d').match(/M([\d.]+) ([\d.]+)/);
        gsap.to(flag, { rotation: 7, duration: 2.4, ease: 'dx-wave', svgOrigin: pin ? `${pin[1]} ${pin[2]}` : '0 0', repeat: -1, repeatDelay: 1.2 });
      });
    };
    const shot = (svg, { delay = 0, duration = 1.7 } = {}) => {
      const line = svg.querySelector('[data-dx-tracer]');
      if (!line || !has('DrawSVGPlugin')) return gsap.timeline();
      const ball = svg.querySelector('[data-dx-ball]');
      const splash = svg.querySelector('[data-dx-splash]');
      const tl = gsap.timeline({ delay });
      const pre = $$('.t-ring, .t-green, .t-fairway, .t-bunker, .t-towel, .t-coin, .t-tee, .t-cup, .t-panel', svg);
      const bars = $$('.t-bars rect', svg);
      const days = $$('.t-days circle', svg);
      const ghosts = $$('.t-ghost', svg);
      const dots = $$('.t-dots circle, .t-land', svg);
      if (pre.length) tl.from(pre, { scale: 0.6, opacity: 0, transformOrigin: '50% 50%', duration: 0.9, stagger: 0.06 }, 0);
      if (bars.length) tl.from(bars, { scaleY: 0, transformOrigin: '50% 100%', duration: 0.8, stagger: 0.06 }, 0.1);
      if (days.length) tl.from(days, { scale: 0, transformOrigin: '50% 50%', duration: 0.6, stagger: 0.07, ease: 'back.out(2)' }, 0.1);
      if (ghosts.length) tl.from(ghosts, { drawSVG: '0%', duration: 1, stagger: 0.12, ease: 'power2.out' }, 0.15);
      if (dots.length) tl.from(dots, { scale: 0, transformOrigin: '50% 50%', duration: 0.4, stagger: 0.05, ease: 'back.out(3)' }, 0.5);
      const slow = has('SlowMo') ? window.SlowMo.ease.config(0.45, 0.42, false) : FLIGHT;
      tl.fromTo(line, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration, ease: slow }, 0.35);
      if (ball && has('MotionPathPlugin')) {
        const hide = ball.hasAttribute('data-dx-ball-hide');
        gsap.set(ball, { transformOrigin: '50% 50%' });
        tl.fromTo(ball, { opacity: 1 }, {
          motionPath: { path: line, align: line, alignOrigin: [0.5, 0.5], start: 0, end: 1 },
          duration, ease: slow, immediateRender: true,
        }, 0.35);
        if (hide) tl.to(ball, { opacity: 0, duration: 0.35 }, 0.35 + duration - 0.35);
      }
      if (splash) tl.fromTo(splash, { scale: 0.2, opacity: 1, transformOrigin: '50% 50%' }, { scale: 2.2, opacity: 0, duration: 0.9, ease: 'power2.out' }, 0.35 + duration - 0.05);
      const roll = svg.querySelector('.t-roll');
      if (roll) tl.from(roll, { drawSVG: '0%', duration: 0.6, ease: 'power2.out' }, 0.35 + duration - 0.1);
      flagWiggle(svg);
      return tl;
    };
    window.DXShot = shot;

    /* ---------- page heads: one orchestrated arrival ---------- */
    const head = document.querySelector('[data-dx-head]');
    const splitHeadline = (el, opts = {}) => {
      if (!SplitText || !el) return null;
      return SplitText.create(el, {
        type: 'lines, words', mask: 'lines', linesClass: 'dx-line', autoSplit: true,
        onSplit(self) {
          return gsap.from(self.lines, {
            yPercent: 110, fontStretch: '78%', duration: 1.25, stagger: 0.11, ease: OUT, delay: opts.delay || 0,
          });
        },
      });
    };
    if (head) {
      const title = head.querySelector('[data-dx-headline]');
      gsap.set(head.querySelectorAll('[data-dx-load]'), { opacity: 1 });
      splitHeadline(title, { delay: 0.05 });
      const rest = $$('[data-dx-load]', head).filter((el) => !el.matches('[data-dx-headline], .dx-head-media, .dx-head-art'));
      gsap.from(rest, { y: 26, opacity: 0, duration: 1.1, stagger: 0.09, delay: 0.3 });
      const typed = head.querySelector('[data-dx-note-visual]');
      const live = head.querySelector('[data-dx-note]');
      if (typed && live) { typed.hidden = false; live.classList.add('dx-sr'); }
      const media = head.querySelector('.dx-head-media');
      if (media) {
        gsap.from(media, { clipPath: 'inset(18% 10% 18% 10% round 28px)', duration: 1.4, ease: 'expo.out', delay: 0.15 });
        gsap.from(media.querySelector('img'), { scale: 1.18, duration: 2, ease: 'expo.out', delay: 0.15 });
      }
      const art = head.querySelector('[data-dx-art]');
      if (art) shot(art, { delay: media ? 0.7 : 0.35, duration: 1.8 });
      const scene = head.querySelector('[data-dx-parallax] img');
      if (scene) {
        gsap.from(scene, { scale: 1.12, duration: 2.4, ease: 'expo.out' });
        gsap.to(head.querySelector('[data-dx-parallax]'), { yPercent: 14, ease: 'none', scrollTrigger: { trigger: head, start: 'top top', end: 'bottom top', scrub: true } });
      }
    }
    gsap.set('[data-dx-load]', { opacity: 1 });
    root.classList.add('dx-go');

    /* ---------- Flip ink: tab bar and segmented sub-nav, carried across page loads ---------- */
    const store = (k, v) => { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; } return null; };
    const tabbar = document.querySelector('[data-dx-tabbar]');
    if (tabbar && Flip) {
      const ink = tabbar.querySelector('.dx-tab-ink');
      const tabs = $$('.dx-tab', tabbar);
      const current = () => tabbar.querySelector('.dx-tab[aria-expanded="true"]') || tabbar.querySelector('.dx-tab[aria-current="page"]');
      const place = (tab) => { ink.style.left = `${tab.offsetLeft + 4}px`; ink.style.width = `${tab.offsetWidth - 8}px`; };
      const prev = Number(store('dx-tab'));
      const cur = current();
      if (ink && cur && !Number.isNaN(prev) && tabs[prev] && tabs[prev] !== cur && !ink.hidden) {
        place(tabs[prev]);
        const state = Flip.getState(ink);
        place(cur);
        Flip.from(state, { duration: 0.6, ease: 'back.out(1.4)' });
      }
      tabs.forEach((t, i) => t.addEventListener('click', () => { if (t.tagName === 'A') store('dx-tab', String(i)); }));
      if (cur) addEventListener('pagehide', () => store('dx-tab', String(tabs.indexOf(cur))));
      document.addEventListener('dx:menu', () => {
        const t = current();
        if (!ink || !t || ink.hidden) return;
        const state = Flip.getState(ink);
        place(t);
        Flip.from(state, { duration: 0.5, ease: 'back.out(1.3)' });
      });
    }
    $$('[data-dx-subnav]').forEach((nav) => {
      const row = nav.querySelector('.dx-subnav-row');
      const links = $$('a', row);
      const cur = row.querySelector('[aria-current="page"]');
      if (!cur) return;
      const ink = document.createElement('span');
      ink.className = 'dx-subnav-ink'; ink.setAttribute('aria-hidden', 'true');
      row.prepend(ink);
      const place = (a) => { ink.style.left = `${a.offsetLeft}px`; ink.style.width = `${a.offsetWidth}px`; };
      const key = `dx-sub-${nav.dataset.dxSubnav}`;
      const prev = links[Number(store(key))];
      place(prev && prev !== cur && Flip ? prev : cur);
      if (prev && prev !== cur && Flip) {
        const state = Flip.getState(ink);
        place(cur);
        Flip.from(state, { duration: 0.7, ease: 'back.out(1.2)', delay: 0.25 });
      }
      links.forEach((a, i) => a.addEventListener('click', () => store(key, String(links.indexOf(cur)))));
      addEventListener('resize', () => place(cur), { passive: true });
    });

    /* ---------- menu: timeline in, timeline out, burger morphs to a cross ---------- */
    const menu = document.querySelector('[data-dx-menu]');
    let menuTl = null;
    const burger = menu && menu.querySelector('[data-dx-burger]');
    const morph = (open) => {
      if (!burger) return;
      load('morphsvg').then(() => {
        if (!window.MorphSVGPlugin) return;
        gsap.registerPlugin(window.MorphSVGPlugin);
        gsap.to(burger, { morphSVG: open ? 'M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5' : 'M4 8.5h16M4 15.5h16', duration: 0.45, ease: 'power3.inOut' });
      });
    };
    if (menu) {
      const panel = menu.querySelector('.dx-menu-panel');
      const scrim = menu.querySelector('.dx-scrim');
      const build = () => {
        const sheet = matchMedia('(max-width: 859px)').matches;
        const tl = gsap.timeline({ paused: true, defaults: { ease: OUT } });
        tl.fromTo(panel, sheet ? { yPercent: 100 } : { clipPath: 'inset(0 0 100% 0)' }, sheet ? { yPercent: 0, duration: 0.6 } : { clipPath: 'inset(0 0 0% 0)', duration: 0.7 }, 0);
        if (scrim && sheet) tl.fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: 0.4 }, 0);
        tl.from(panel.querySelectorAll('.dx-menu-search, .dx-menu-cols > div, .dx-install:not([hidden])'), { y: 18, opacity: 0, duration: 0.6, stagger: 0.06 }, 0.12);
        tl.from(panel.querySelectorAll('.dx-menu-cols a'), { x: -10, opacity: 0, duration: 0.5, stagger: 0.012 }, 0.2);
        return tl;
      };
      menu.addEventListener('toggle', () => {
        if (menu.open) {
          menuTl?.kill();
          menuTl = build();
          menuTl.play(0);
          lenis?.stop();
        } else {
          lenis?.start();
          gsap.set([panel, scrim], { clearProps: 'all' });
        }
        morph(menu.open);
      });
      window.DXMotion = window.DXMotion || {};
      window.DXMotion.closeMenu = (done) => {
        if (!menuTl) return done();
        menuTl.timeScale(1.8).reverse().eventCallback('onReverseComplete', () => { menuTl.timeScale(1); done(); });
      };
    }
    window.DXMotion = Object.assign(window.DXMotion || {}, { scrollTo, lenis, shot, load });

    /* ---------- section headings: masked line reveals, once ---------- */
    $$('[data-dx-split]').forEach((el) => {
      if (!SplitText || el.closest('[data-dx-head]')) return;
      SplitText.create(el, {
        type: 'lines', mask: 'lines', linesClass: 'dx-line', autoSplit: true,
        onSplit(self) {
          return gsap.from(self.lines, { yPercent: 105, duration: 1.1, stagger: 0.09, scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
        },
      });
    });

    /* ---------- grouped reveals (used sparingly in markup) ---------- */
    $$('[data-dx-stagger]').forEach((group) => {
      const kids = [...group.children].filter((k) => k.nodeType === 1);
      gsap.from(kids, { y: 34, opacity: 0, duration: 1, stagger: 0.08, scrollTrigger: { trigger: group, start: 'top 86%', once: true } });
    });
    $$('[data-dx-reveal]').forEach((el) => {
      gsap.from(el, { y: 30, opacity: 0, duration: 1, scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
    });

    /* ---------- photographs open like a shutter ---------- */
    $$('[data-dx-photo]').forEach((box) => {
      const img = box.querySelector('img');
      const tl = gsap.timeline({ scrollTrigger: { trigger: box, start: 'top 85%', once: true } });
      tl.from(box, { clipPath: 'inset(14% 8% 14% 8% round 28px)', duration: 1.3, ease: 'expo.out' });
      if (img) {
        tl.from(img, { scale: 1.25, duration: 1.8, ease: has('ExpoScaleEase') ? window.ExpoScaleEase.config(1.25, 1, 'expo.out') : 'expo.out' }, 0);
      }
    });
    $$('[data-dx-parallax]').forEach((el) => {
      if (el.closest('[data-dx-head]')) return;
      const k = parseFloat(el.dataset.dxParallax) || 0.15;
      gsap.fromTo(el, { yPercent: -k * 50 }, { yPercent: k * 50, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    /* ---------- art outside the page head plays when it arrives ---------- */
    $$('[data-dx-art]').forEach((svg) => {
      if (svg.closest('[data-dx-head]') || svg.closest('[data-dx-scorecard]')) return;
      const tl = shot(svg);
      tl.pause();
      ScrollTrigger.create({ trigger: svg, start: 'top 82%', once: true, onEnter: () => tl.play() });
    });

    /* ---------- numbers: count up or scramble into place ---------- */
    const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en');
    $$('[data-dx-count]').forEach((el) => {
      const to = Number(el.dataset.dxCount);
      if (!Number.isFinite(to)) return;
      const o = { v: 0 };
      el.textContent = fmt.format(0);
      gsap.to(o, { v: to, duration: to > 50 ? 1.8 : 1.1, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true }, onUpdate: () => { el.textContent = fmt.format(Math.round(o.v)); } });
    });
    $$('[data-dx-scramble]').forEach((el) => {
      if (!has('ScrambleTextPlugin')) return;
      const text = el.textContent;
      gsap.from(el, { scrambleText: { text: ' ', chars: '0123456789', speed: 0.6 }, duration: 1.2, scrollTrigger: { trigger: el, start: 'top 90%', once: true }, onComplete: () => { el.textContent = text; } });
    });

    /* ---------- scores you are chasing get struck through by the tracer ---------- */
    $$('[data-dx-strike]').forEach((row) => {
      const line = row.querySelector('.dx-strike-line');
      if (!line || !has('DrawSVGPlugin')) return;
      gsap.fromTo(line, { drawSVG: '0%' }, { drawSVG: '100%', duration: 0.9, ease: 'power3.inOut', scrollTrigger: { trigger: row, start: 'top 72%', once: true } });
    });

    /* ---------- rails: drag with momentum on desktop, native swipe on touch ---------- */
    const rails = $$('[data-dx-rail]');
    if (rails.length && fine()) {
      load('draggable', 'inertia').then(() => {
        if (!window.Draggable) return;
        if (window.InertiaPlugin) gsap.registerPlugin(window.Draggable, window.InertiaPlugin);
        rails.forEach((rail) => {
          if (rail.scrollWidth <= rail.clientWidth + 4) return;
          window.Draggable.create(rail, {
            type: 'scrollLeft', inertia: !!window.InertiaPlugin, edgeResistance: 0.85, throwResistance: 2600,
            cursor: 'grab', activeCursor: 'grabbing', allowContextMenu: true, minimumMovement: 6,
            onDragStart() { rail.classList.add('is-dragging'); },
            onRelease() { if (!this.isThrowing) rail.classList.remove('is-dragging'); },
            onThrowComplete() { rail.classList.remove('is-dragging'); },
          });
          rail.addEventListener('click', (e) => { if (rail.dataset.dragged === '1') { e.preventDefault(); rail.dataset.dragged = ''; } }, true);
          rail.addEventListener('pointermove', (e) => { if (e.buttons) rail.dataset.dragged = '1'; });
          rail.addEventListener('pointerdown', () => { rail.dataset.dragged = ''; });
        });
      });
    }

    /* ---------- tickers: an endless band whose speed follows the scroll ---------- */
    const tracks = $$('.dx-ticker-track').map((track) => {
      const t = gsap.to(track, { xPercent: -50, ease: 'none', duration: 60, repeat: -1 });
      t.totalTime(t.duration() * 200); /* room to run backwards when the reader scrolls up */
      return t;
    });
    if (tracks.length && Observer) {
      let boost = gsap.to({}, { duration: 0.1 });
      Observer.create({
        target: window, type: 'wheel,touch,scroll',
        onChangeY(self) {
          const v = Math.min(6, 1 + Math.abs(self.velocityY) / 500);
          const dir = self.deltaY < 0 ? -1 : 1;
          boost.kill();
          tracks.forEach((t) => t.timeScale(v * dir));
          boost = gsap.to(tracks, { timeScale: dir, duration: 1.2, ease: 'power2.out', overwrite: true });
        },
      });
    }

    /* ---------- disclosures open and close smoothly ---------- */
    $$('details.dx-disclosure').forEach((d) => {
      const summary = d.querySelector(':scope > summary');
      const body = d.querySelector(':scope > .dx-disclosure-body');
      if (!summary || !body) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d.open) {
          gsap.to(body, { height: 0, opacity: 0, duration: 0.45, ease: 'power3.inOut', onComplete: () => { d.open = false; gsap.set(body, { clearProps: 'height,opacity' }); } });
        } else {
          d.open = true;
          gsap.from(body, { height: 0, opacity: 0, duration: 0.6, ease: OUT, clearProps: 'height,opacity' });
        }
      });
    });

    /* ---------- plan pickers: the highlight glides to the chosen plan ---------- */
    document.addEventListener('dx:plan', (e) => {
      const rootEl = e.detail.root;
      const glide = rootEl.querySelector('.dx-plan-glide');
      const on = rootEl.querySelector('input:checked')?.closest('label');
      if (glide && on && Flip) {
        const state = Flip.getState(glide);
        on.append(glide);
        Flip.from(state, { duration: 0.55, ease: 'back.out(1.25)', absolute: true });
      }
      const total = rootEl.querySelector('[data-dx-plan-panel]:not([hidden]) [data-dx-total]');
      if (total && has('ScrambleTextPlugin')) gsap.from(total, { scrambleText: { text: total.textContent.replace(/\d/g, '0'), chars: '0123456789', speed: 0.8 }, duration: 0.7, onComplete() { total.textContent = total.dataset.dxTotal; } });
      const panel = rootEl.querySelector('[data-dx-plan-panel]:not([hidden])');
      if (panel) gsap.from(panel.querySelectorAll('li, .dx-plan-sum-row'), { x: 14, opacity: 0, duration: 0.5, stagger: 0.05 });
    });

    /* ---------- the home scorecard: each clean strike flies to the green ---------- */
    $$('[data-dx-scorecard]').forEach((card) => {
      const stage = card.querySelector('[data-dx-stage]');
      const shotsG = stage && stage.querySelector('[data-dx-shots]');
      const counter = card.querySelector('[data-dx-today]');
      const typed = card.querySelector('[data-dx-note-visual]');
      const live = card.querySelector('[data-dx-note]');
      if (!stage || !shotsG) return;
      const NS = 'http://www.w3.org/2000/svg';
      const tee = (stage.dataset.tee || '60,250').split(',').map(Number);
      const green = (stage.dataset.green || '470,120,60,16').split(',').map(Number);
      const bounce = load('custombounce').then(() => {
        if (!window.CustomBounce) return null;
        gsap.registerPlugin(window.CustomBounce);
        window.CustomBounce.create('dx-land', { strength: 0.55, squash: 2, squashID: 'dx-land-squash' });
        return true;
      });
      load('text');
      const marks = new Map();
      const typeNote = () => {
        if (!typed || !live) return;
        if (window.TextPlugin) { gsap.registerPlugin(window.TextPlugin); gsap.to(typed, { text: { value: live.textContent }, duration: Math.min(1.4, live.textContent.length / 55), ease: 'none', overwrite: true }); }
        else typed.textContent = live.textContent;
      };
      const fly = (index, miss) => {
        const ang = (index * 137.5) % 360 * Math.PI / 180;
        const r = miss ? 1.35 + (index % 3) * 0.12 : 0.15 + ((index * 0.37) % 0.7);
        const lx = green[0] + Math.cos(ang) * green[2] * r;
        const ly = green[1] + Math.sin(ang) * green[3] * r;
        const apex = Math.min(tee[1], ly) - 120 - (index % 4) * 14;
        const d = `M${tee[0]} ${tee[1]}C${tee[0] + 40} ${apex} ${lx - 90} ${apex - 10} ${lx} ${ly}`;
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', d); path.setAttribute('class', 't-line t-line--shot');
        const ball = document.createElementNS(NS, 'circle');
        ball.setAttribute('r', '5'); ball.setAttribute('class', 't-ball');
        const mark = document.createElementNS(NS, 'circle');
        mark.setAttribute('cx', lx); mark.setAttribute('cy', ly); mark.setAttribute('r', '4.5');
        mark.setAttribute('class', 't-land t-land--card');
        shotsG.append(path, ball);
        const tl = gsap.timeline();
        tl.fromTo(path, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 0.9, ease: 'power1.inOut' });
        tl.fromTo(ball, { opacity: 1 }, { motionPath: { path, align: path, alignOrigin: [0.5, 0.5] }, duration: 0.9, ease: 'power1.inOut', immediateRender: true }, 0);
        tl.add(() => {
          shotsG.append(mark);
          bounce.then((ok) => gsap.from(mark, ok ? { y: -16, duration: 0.7, ease: 'dx-land' } : { scale: 0, transformOrigin: '50% 50%', duration: 0.4, ease: 'back.out(3)' }));
        });
        tl.to(path, { drawSVG: '100% 100%', duration: 0.7, ease: 'power2.in' }, 0.95);
        tl.to(ball, { opacity: 0, duration: 0.2 }, 0.9);
        tl.add(() => { path.remove(); ball.remove(); });
        return mark;
      };
      document.addEventListener('dx:attempt', (e) => {
        if (e.detail.card !== card) return;
        const { ball, clean, count, index } = e.detail;
        if (clean) {
          marks.set(index, fly(index, false));
          gsap.fromTo(ball, { scale: 0.8 }, { scale: 1, duration: 0.6, ease: 'back.out(3)' });
        } else {
          const m = marks.get(index);
          if (m) gsap.to(m, { scale: 0, opacity: 0, transformOrigin: '50% 50%', duration: 0.3, onComplete: () => m.remove() });
          marks.delete(index);
        }
        if (counter && has('ScrambleTextPlugin')) gsap.to(counter, { scrambleText: { text: String(count), chars: '0123456789', speed: 1 }, duration: 0.5 });
        typeNote();
        if (count === 10) celebrate(card);
      });
      document.addEventListener('dx:attempt-reset', (e) => {
        if (e.detail.card !== card) return;
        marks.forEach((m) => m.remove()); marks.clear();
        typeNote();
      });
      /* opening shot: one demonstration flight when the card first arrives */
      ScrollTrigger.create({ trigger: card, start: 'top 80%', once: true, onEnter: () => {
        const intro = stage.querySelector('[data-dx-art-intro]');
        if (intro) shot(intro, { duration: 1.5 });
      } });
    });

    /* ---------- celebration: a burst of balls (Physics2D) ---------- */
    const celebrate = (host) => {
      load('physics2d').then(() => {
        if (!window.Physics2DPlugin) return;
        gsap.registerPlugin(window.Physics2DPlugin);
        const box = host.getBoundingClientRect();
        const layer = document.createElement('div');
        layer.className = 'dx-burst'; layer.setAttribute('aria-hidden', 'true');
        layer.style.left = `${box.left + box.width / 2}px`; layer.style.top = `${box.top + box.height * 0.4}px`;
        document.body.append(layer);
        const colors = ['#d8ee86', '#f4f5ed', '#f3c94f', '#8cc6d9', '#e3d8b8'];
        for (let i = 0; i < 46; i += 1) {
          const p = document.createElement('i');
          p.style.background = colors[i % colors.length];
          const s = 5 + Math.random() * 7; p.style.width = `${s}px`; p.style.height = `${s}px`;
          layer.append(p);
          gsap.to(p, { duration: 1.6 + Math.random(), physics2D: { velocity: 380 + Math.random() * 420, angle: -90 + (Math.random() - 0.5) * 120, gravity: 900 }, rotation: Math.random() * 360, opacity: 0, ease: 'none' });
        }
        gsap.delayedCall(2.8, () => layer.remove());
      });
    };
    window.DXMotion.celebrate = celebrate;

    /* ---------- welcome: the finish replays the putt and drops it in the cup ---------- */
    const finish = document.getElementById('dvw-step-3');
    if (finish) {
      new MutationObserver(() => {
        if (finish.hidden) return;
        const art = document.querySelector('.dx-welcome-art [data-dx-art]');
        if (art) shot(art, { duration: 1.3 });
        const mark = finish.querySelector('.dvw-done');
        if (mark) gsap.from(mark, { scale: 0.3, opacity: 0, duration: 0.7, ease: 'back.out(2.2)' });
        gsap.delayedCall(0.35, () => celebrate(mark || finish));
      }).observe(finish, { attributes: true, attributeFilter: ['hidden'] });
    }
    document.addEventListener('click', (e) => {
      const done = e.target.closest('[data-complete]');
      if (done && done.closest('#skills-app')) setTimeout(() => celebrate(document.querySelector('#skills-cards .skills-current') || done), 120);
    });

    /* ---------- invalid fields get a small shake ---------- */
    document.addEventListener('invalid', (e) => {
      const el = e.target;
      load('customwiggle').then(() => {
        if (!window.CustomWiggle) return;
        gsap.registerPlugin(window.CustomWiggle);
        if (!gsap.parseEase('dx-shake')) window.CustomWiggle.create('dx-shake', { wiggles: 5, type: 'easeOut' });
        gsap.fromTo(el, { x: 0 }, { x: 7, duration: 0.5, ease: 'dx-shake', clearProps: 'x' });
      });
    }, true);

    /* ---------- guide timelines: the tracer runs down the steps as you read ---------- */
    $$('[data-dx-timeline]').forEach((list) => {
      const fill = document.createElement('span');
      fill.className = 'dx-tl-fill'; fill.setAttribute('aria-hidden', 'true');
      list.prepend(fill);
      gsap.fromTo(fill, { scaleY: 0 }, { scaleY: 1, ease: 'none', scrollTrigger: { trigger: list, start: 'top 72%', end: 'bottom 62%', scrub: 0.4 } });
      $$(':scope > li', list).forEach((li) => {
        ScrollTrigger.create({ trigger: li, start: 'top 66%', onEnter: () => li.classList.add('is-lit'), onLeaveBack: () => li.classList.remove('is-lit') });
      });
    });

    /* ---------- reading progress on long guides ---------- */
    const progress = document.querySelector('[data-dx-progress]');
    if (progress) {
      const bar = document.createElement('div');
      bar.className = 'dx-progress'; bar.setAttribute('aria-hidden', 'true');
      document.body.append(bar);
      gsap.fromTo(bar, { scaleX: 0 }, { scaleX: 1, ease: 'none', scrollTrigger: { trigger: progress, start: 'top 20%', end: 'bottom 80%', scrub: 0.3 } });
    }

    /* ---------- app content arrives with a little life ---------- */
    const appRoots = $$('#skills-cards, #session-history, #round-list, #dv-find-results, #tracker-compare-results, #dv-drill-trends, #practice-list, .dws, #dv-my-plan');
    if (appRoots.length) {
      const seen = new WeakSet();
      const mo = new MutationObserver((list) => {
        const fresh = [];
        list.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1 && !seen.has(n) && n.offsetParent !== null && !n.closest('dialog')) { seen.add(n); fresh.push(n); } }));
        if (fresh.length && fresh.length < 40) gsap.from(fresh, { y: 14, opacity: 0, duration: 0.55, stagger: 0.03, clearProps: 'transform,opacity' });
      });
      appRoots.forEach((r) => mo.observe(r, { childList: true }));
    }

    /* keep measurements right once fonts and late images settle */
    document.fonts?.ready.then(() => ScrollTrigger.refresh());
    addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  });
})();
